import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getCallbackReport } from '../../../store/ReportsDetailsSlice/actions';
import CallbackReportTable from '../../../components/Reports/ReportsTable';
import useCallbackReportColumns from './hooks/useCallbackReportColumns';
import useCallbackReportSummaryColumns from './hooks/useCallbackReportSummaryColumns';
import FillTimePieChart from '../../../components/Reports/FillTimePieChart';
import CumulativeFillTimeChart from '../../../components/Reports/CumulativeFillTimeChart';
import { selectCallbackData } from '../../../store/ReportsDetailsSlice/selectors';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const ReportCallback = () => {
  const dispatch = useDispatch();
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { callbackReportData, loadingCallbackReport } =
    useSelector(selectCallbackData);

  useEffect(() => {
    if (fromDate && toDate) {
      const start_dt = moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00';
      const end_dt = moment(toDate).format('YYYY-MM-DD') + ' 23:59:59';

      const params = {
        start_dt,
        end_dt,
      };
      dispatch(getCallbackReport(params));
    }
  }, [dispatch, fromDate, toDate]);

  const columns = useCallbackReportColumns();
  const columnsSummary = useCallbackReportSummaryColumns();

  const employeesRows = (callbackReportData?.employees || []).map((item) => {
    const cleaned = item.timestamp.replace(' (UTC)', '');

    const timestamp = moment.utc(cleaned, 'DD/MM/YYYY HH:mm:ss');

    return {
      ...item,
      timestamp: timestamp.format('MM/DD/YYYY HH:mm:ss'),
    };
  });

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Callback Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container spacing={2} className="my-2">
          <Grid item xs={12} className="d-flex justify-content-between">
            <Grid container sx={{ width: '100%' }}>
              <Grid
                item
                sx={{
                  display: 'flex',
                  gap: 2,
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    flexDirection: isTabletWidth ? 'column' : 'row',
                    gap: 2,
                    width: '100%',
                    mt: 'auto',
                  }}
                >
                  {render}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {loadingCallbackReport ? (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            ) : callbackReportData?.employees?.length === 0 ? (
              <DataNotFound />
            ) : (
              <Box>
                <Grid container spacing={3} className="my-2">
                  <Grid item xs={12} xl={4}>
                    <Typography variant="h6">Fill Time</Typography>
                    <FillTimePieChart data={callbackReportData?.employees} />
                  </Grid>
                  <Grid item xs={12} xl={8}>
                    <Typography variant="h6">Cumulative Fill Time</Typography>
                    <CumulativeFillTimeChart
                      data={callbackReportData?.employees}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CallbackReportTable
                      rows={employeesRows || []}
                      columns={columns}
                      getRowId={(row) => row.timestamp + row.create_name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CallbackReportTable
                      rows={callbackReportData?.accepted || []}
                      columns={columnsSummary}
                      getRowId={(row) =>
                        row.callbacks_accepted + row.accepted_by
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ReportCallback;
